<template>
  <div>
    <b-row class="mb-4">
      <b-col>
        <b-form-group
          v-if="externalSurveyInstanceId"
          :label="$t('Webportal.SurveyInstanceId')"
          label-size="sm"
        >
          <span class="fw-bold">{{ externalSurveyInstanceId }}</span>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          v-if="surveyInstance"
          :label="$t('Webportal.Survey')"
          label-size="sm"
        >
          {{ surveyInstance.Survey.Title }}
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          v-if="statusId != null"
          :label="$t('Webportal.Status')"
          label-size="sm"
        >
            <div class="d-flex align-items-start gap-2">

          <b-dropdown
            v-if="isStatusSelectionEnabled"
            size="sm"
            variant="primary"
            link-class="fw-bold"
          >
            <template #button-content>
              <span class="fw-bold">
                <font-awesome-icon
                  :icon="['far', getImprovementStatusIcon(statusId)]"
                />
                {{ getImprovementStatusText(statusId) }}
              </span>
            </template>

            <b-dropdown-item  v-for="nextStatus in surveyImprovement.NextAllowedSurveyImprovementStatuses"
                              :key="nextStatus"
                              @click="nextStatusClicked(nextStatus)">
              <font-awesome-icon :icon="['far', getImprovementStatusIcon(nextStatus)]" />
              {{ getImprovementStatusText(nextStatus) }}
            </b-dropdown-item>
          </b-dropdown>

          <span v-else class="fw-bold">
            <template v-if="surveyImprovement">
              <font-awesome-icon
                :icon="['far', getImprovementStatusIcon(statusId)]"
              />
              {{ getImprovementStatusText(statusId) }}
            </template>
            <template v-else>
              {{ getSurveyInstanceStatusText(statusId) }}
            </template>
          </span>

          <template v-if="surveyImprovement && statusHistoryVisible">
              <b-button
                id="popover-status-history"
                class="ml-2"
                size="sm"
                variant="outline-primary"
              >
                <font-awesome-icon :icon="['fas', 'history']" />
              </b-button>
              <b-popover
                target="popover-status-history"
                triggers="hover focus"
                placement="bottom"
                custom-class="mw-100"
                :title="$t('Webportal.StatusHistory')"
              >
                <b-list-group
                  v-if="surveyImprovement.History && surveyImprovement.History.length > 0" flush
                >
                  <b-list-group-item
                    v-for="(history, index) in surveyImprovement.History"
                    :key="index"
                  >
                    <b-row>
                      <b-col>
                        <span class="fw-bold">
                          <font-awesome-icon
                            :icon="[
                              'far',
                              getImprovementStatusIcon(history.StatusId),
                            ]"
                          />
                          {{ getImprovementStatusText(history.StatusId) }}
                        </span>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col cols="auto">
                        {{ msm_datetime(history.ModifiedAt) }}
                      </b-col>
                      <b-col>
                        {{ getModifierInfo(history) }}
                      </b-col>
                    </b-row>
                  </b-list-group-item>
                </b-list-group>
                <span v-else>
                  {{ $t("Webportal.NoDataFoundPlain") }}
                </span>
              </b-popover>

          </template>
            </div>

        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="surveyInstance">
      <b-col>
        <b-row>
          <b-col>
            <b-form-group :label="$t('Webportal.Customer')" label-size="sm">
              {{ surveyInstance.Client.Name }}
            </b-form-group>
          </b-col>
          <b-col>
            <b-form-group :label="$t('Webportal.LocationId')" label-size="sm">
              {{ surveyInstance.ClientLocation.ClientLocationIdOnClientSite }}
            </b-form-group>
          </b-col>
        </b-row>
        <p v-if="!surveyImprovement && notificationHint">
          <span class="text-danger">
            <font-awesome-icon :icon="['fas', 'info-circle']" /> {{ notificationHint }}:
          </span>
          <b-link :to="notificationSettingsRoute">{{ $t("Webportal.NotificationSettings") }}</b-link>
        </p>
      </b-col>
      <b-col cols="4">
        <b-form-group :label="$t('Webportal.LocationAddress')" label-size="sm">
          {{ surveyInstance.ClientLocation.Name }}<br />
          {{ surveyInstance.ClientLocation.Address }}<br />
          <span v-if="surveyInstance.ClientLocation.Address2">
            {{ surveyInstance.ClientLocation.Address2 }}
            <br />
          </span>
          {{ surveyInstance.ClientLocation.City }},
          {{ surveyInstance.ClientLocation.CountryId }} -
          {{ surveyInstance.ClientLocation.PostalCode }}
        </b-form-group>
      </b-col>
    </b-row>

    <!-- Modal: Confirm Status Change -->
    <b-modal
      ref="modalConfirmStatusChange"
      :title="$t('Webportal.Dialog.ConfirmStatusChange.Title')"
      :ok-title="$t('Webportal.Dialog.Ok')"
      :cancel-title="$t('Webportal.Dialog.Cancel')"
      @ok="confirmStatusChange"
    >
      <div v-html="$t('Webportal.Dialog.ConfirmStatusChange.Text', {
        status: `<b>${pendingStatusText}</b>`
      })"></div>
    </b-modal>
  </div>
</template>

<script>
import SurveyInstanceMixin from '@/mixins/SurveyInstanceMixin';
import SurveyImprovementMixin from "@/mixins/SurveyImprovementMixin";
import CustomFilter from '@/plugins/CustomFilter';

export default {
  name: "SurveyImprovementHeader",

  mixins: [ SurveyInstanceMixin, SurveyImprovementMixin ],

  props: {
    surveyImprovement: null,
    surveyInstance: null,
    statusSelection: { default: false },
    statusHistoryVisible: { default: true },
    relatedSurveyImprovementsVisible: { default: true },
  },

  data() {
    return {
      pendingNextStatusId: null,
      pendingStatusText: '',
    };
  },

  computed: {
    externalSurveyInstanceId() {
      return (
        this.surveyImprovement?.ExternalSurveyInstanceId ??
        this.surveyInstance?.ExternalSurveyInstanceId ??
        this.surveyInstance?.SurveyInstance?.ExternalSurveyInstanceId
      );
    },

    statusId() {
      return (
        this.surveyImprovement?.StatusId ??
        this.surveyInstance?.SurveyInstance?.SurveyInstanceStatusId
      );
    },

    isStatusSelectionEnabled() {
      return (
        this.statusSelection && !_.isEmpty(this.surveyImprovement?.NextAllowedSurveyImprovementStatuses)
      );
    },

    notificationHint() {
      return this.$t('Webportal.Actions.SurveyImprovement.NotificationNotice');
    }
  },

  methods: {
    msm_datetime(val) {
      return CustomFilter.msm_datetime(val)
    },

    onStatusChanged(value) {
      this.$emit("statusChanged", value);
    },

    nextStatusClicked(nextStatusId) {
      // Show confirmation dialog before setting final status
      if (
        nextStatusId == this.SurveyImprovementStatus.Approved ||
        nextStatusId == this.SurveyImprovementStatus.Revised ||
        nextStatusId == this.SurveyImprovementStatus.Done ||
        // nextStatusId == this.SurveyImprovementStatus.Dismissed ||
        nextStatusId == this.SurveyImprovementStatus.RevisedApproved ||
        nextStatusId == this.SurveyImprovementStatus.RevisedDismissed
      ) {
        // Speichere den Status für die Confirmation
        this.pendingNextStatusId = nextStatusId;
        this.pendingStatusText = this.getImprovementStatusText(nextStatusId);

        // Zeige "Confirm Status Change" Modal
        this.$refs.modalConfirmStatusChange.show();
      } else {
        this.onStatusChanged(nextStatusId);
      }
    },

    confirmStatusChange() {
      // Wird aufgerufen, wenn der User OK klickt
      if (this.pendingNextStatusId) {
        this.onStatusChanged(this.pendingNextStatusId);
        this.pendingNextStatusId = null;
        this.pendingStatusText = '';
      }
    },

    getModifierInfo(history) {
      if (!history.ModifiedBy) return '';

      if (!history.ModifiedBy.Email)
        return `${history.ModifiedBy.FirstName} ${history.ModifiedBy.LastName}`;
      return `${history.ModifiedBy.FirstName} ${history.ModifiedBy.LastName} (${history.ModifiedBy.Email})`;
    }
  },
};
</script>
